/**
 * CollectionDayPage
 * Daily collection detail: date picker, summary stats, and full data table
 */

import { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  CalendarCheck,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { StatCard } from '@/components/data-display/StatCard';
import { DataTable } from '@/features/credit/components/DataTable';
import { ROUTES } from '@/constants';
import { HelpButton } from '@/features/help';
import { useCollectionDashboard } from '@/features/collection/hooks';
import {
  formatCurrencyMXN,
  COLLECTION_STATUS_LABEL,
  COLLECTION_STATUS_VARIANT,
  PAYMENT_TYPE_LABEL,
  PAYMENT_TYPE_VARIANT,
} from '@/features/collection/types';
import type { CollectionRecord } from '@/types/collection.types';
import { formatNumber } from '@/utils/formatters';

// ─── Constants ───────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Dia de Cobro', path: ROUTES.COLLECTION_DAY },
];

// Use local date (not UTC) to avoid timezone offset issues
const now = new Date();
const TODAY = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

// ─── Table Columns ───────────────────────────────────────────────────────────

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const columns: ColumnDef<CollectionRecord, unknown>[] = [
  {
    accessorKey: 'collection_date',
    header: 'Fecha',
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap text-xs">{formatDateShort(getValue<string>())}</span>
    ),
  },
  {
    accessorKey: 'credit_id',
    header: 'Credito',
    cell: ({ getValue }) => (
      <span className="font-mono text-xs">{getValue<string>()}</span>
    ),
  },
  {
    accessorKey: 'coupon_number',
    header: 'Cupon',
  },
  {
    accessorKey: 'amount_requested_mxn',
    header: 'Esperado',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{formatCurrencyMXN(getValue<number>())}</span>
    ),
  },
  {
    accessorKey: 'amount_collected_mxn',
    header: 'Cobrado',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{formatCurrencyMXN(getValue<number>())}</span>
    ),
  },
  {
    accessorKey: 'iva_collected_mxn',
    header: 'IVA',
    cell: ({ getValue }) => (
      <span className="tabular-nums">{formatCurrencyMXN(getValue<number>() ?? 0)}</span>
    ),
  },
  {
    accessorKey: 'collection_status',
    header: 'Estado',
    cell: ({ getValue }) => {
      const status = getValue<string>();
      return (
        <Badge
          variant={(COLLECTION_STATUS_VARIANT[status] ?? 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info'}
        >
          {COLLECTION_STATUS_LABEL[status] ?? status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'payment_type',
    header: 'Tipo Pago',
    cell: ({ getValue }) => {
      const type = getValue<string>();
      return (
        <Badge
          variant={(PAYMENT_TYPE_VARIANT[type] ?? 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info'}
        >
          {PAYMENT_TYPE_LABEL[type] ?? type}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'transfer_status',
    header: 'Transferencia',
    cell: ({ getValue }) => (
      <span className="text-xs text-muted-foreground">{getValue<string>()}</span>
    ),
  },
];

// ─── Skeleton Loading State ──────────────────────────────────────────────────

function DaySkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton variant="rectangular" height={40} width="200px" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`stat-skeleton-${i.toString()}`}
            className="rounded-lg border border-border bg-card p-5"
          >
            <Skeleton variant="text" width="60%" height={14} />
            <div className="mt-3">
              <Skeleton variant="text" width="50%" height={28} />
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border bg-card p-6">
        <Skeleton variant="rectangular" height={200} />
      </div>
    </div>
  );
}

// ─── Error State ─────────────────────────────────────────────────────────────

function DayError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar el dia de cobro
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function CollectionDayPage() {
  const [selectedDate, setSelectedDate] = useState<string>(TODAY);

  const { daily, isLoading, isError, refetchAll } =
    useCollectionDashboard(selectedDate);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Dia de Cobro"
          subtitle="Detalle de cupones procesados"
          breadcrumbs={BREADCRUMBS}
        />
        <DaySkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader
          title="Dia de Cobro"
          subtitle="Detalle de cupones procesados"
          breadcrumbs={BREADCRUMBS}
        />
        <DayError onRetry={refetchAll} />
      </div>
    );
  }

  // Use data from the daily response
  const allRecords = daily?.records ?? [];

  return (
    <div>
      <PageHeader
        title="Dia de Cobro"
        subtitle="Detalle de cupones procesados"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="collection-day" />
            <Button onClick={refetchAll}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* ── Date Picker ──────────────────────────────────────────────── */}
        <div className="max-w-xs">
          <Input
            label="Fecha de cobro"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        {/* ── Summary Stats ────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Cupones"
            value={formatNumber(allRecords.length)}
            icon={CalendarCheck}
          />
          <StatCard
            title="Cobrados"
            value={formatNumber(daily?.summary.full.count ?? 0)}
            icon={CalendarCheck}
          />
          <StatCard
            title="Parciales"
            value={formatNumber(daily?.summary.partial.count ?? 0)}
            icon={CalendarCheck}
          />
          <StatCard
            title="Sin Cobro"
            value={formatNumber(daily?.summary.none.count ?? 0)}
            icon={AlertTriangle}
          />
        </div>

        {/* ── Full Data Table ──────────────────────────────────────────── */}
        <DataTable<CollectionRecord>
          data={allRecords}
          columns={columns}
          searchPlaceholder="Buscar por credito..."
          emptyMessage="Sin cupones para la fecha seleccionada"
          pageSize={25}
        />
      </div>
    </div>
  );
}
